/**
 * 木の並び順を決める位置文字列。9 桁ゼロ詰めの整数で、辞書順がそのまま表示順になる。
 *
 * Swift 版（`Packages/BisideCore/Sources/BisideCore/Indexing/FractionIndex.swift`）の対応実装。
 * **両者は `cloud/fixtures/fraction-index.json` で照合される**ので、片方だけ挙動を変えられない。
 *
 * この計算をクライアント側に置くのは意図的で、挿入位置は送る側が決めて操作に載せる。サーバは
 * 検証するだけで計算しない（同じ計算を三か所に持たないため）。
 */

const WIDTH = 9;
const GAP = 1000;

/** Swift の `Int(String)` と同じ厳しさで読む。数字以外が混じったら「読めない」。 */
function parseIntStrict(s: string | null | undefined): number | null {
  if (s == null) return null;
  if (!/^[+-]?\d+$/.test(s)) return null;
  const v = Number(s);
  return Number.isSafeInteger(v) ? v : null;
}

function pad(n: number): string {
  return n < 0 ? "-" + String(-n).padStart(WIDTH, "0") : String(n).padStart(WIDTH, "0");
}

/**
 * `prev` と `next` の間に入る位置を返す。`prev` が null なら先頭へ、`next` が null なら末尾へ。
 *
 * 隙間が 1 以下になると `prev` と同値を返す。これは欠陥ではなく**枯渇の合図**で、
 * 呼び出し側は `isExhausted` か `reorder` の再配置でこれを検出する。
 */
export function between(prev: string | null, next: string | null): string {
  const lo = parseIntStrict(prev) ?? 0;
  const parsedNext = next != null ? parseIntStrict(next) : null;
  const hi = parsedNext ?? lo + GAP * 2;
  const mid = lo + Math.trunc((hi - lo) / 2);
  return pad(mid);
}

/** `count` 件を均等に配置し直す。 */
export function rebalance(count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(pad((i + 1) * GAP));
  return out;
}

/** 隣り合う位置の間に、もう入る余地が無いか。 */
export function isExhausted(prev: string, next: string): boolean {
  const lo = parseIntStrict(prev);
  const hi = parseIntStrict(next);
  if (lo == null || hi == null) return false;
  return hi - lo < 2;
}

/** 手で並べ替えたときに書き込むべき内容。 */
export type ReorderPlan =
  | { kind: "single"; position: string }
  | { kind: "rebalanced"; indices: number[]; positions: string[] };

/**
 * `from` 番目を `to` の位置へ動かす。`from` が範囲外なら null。
 *
 * `to` は**動かす行を抜いたあとのリスト**に対して丸められる。これが「一つ右へ」を隣の行自身の
 * 添字として扱える理由。隙間が尽きて `between` が両隣と同値を返したときは、全体を再配置する
 * 計画に切り替える（同値のままだと安定ソートで並びが変わらず、ドラッグが黙って無効になる）。
 */
export function reorder(positions: string[], from: number, to: number): ReorderPlan | null {
  if (from < 0 || from >= positions.length) return null;

  const order: number[] = positions.map((_, i) => i);
  order.splice(from, 1);
  const clamped = Math.max(0, Math.min(to, order.length));

  const prev = clamped > 0 ? positions[order[clamped - 1]] : null;
  const next = clamped < order.length ? positions[order[clamped]] : null;
  const candidate = between(prev, next);

  const collides =
    (prev != null && candidate <= prev) || (next != null && candidate >= next);
  if (!collides) return { kind: "single", position: candidate };

  order.splice(clamped, 0, from);
  return { kind: "rebalanced", indices: order, positions: rebalance(order.length) };
}
